import React, { useState, useEffect } from 'react';

// 現場での役割（ADR 0014）。キャッチャーは着座まで、クローザーは着座後のクロージング担当。
export const JOB_TYPE_LABELS = {
  catcher: 'キャッチャー',
  closer: 'クローザー',
  both: 'キャッチャー&クローザー',
};
import { api } from '../utils/api';

// NS_KPI シートに載せるか（ADR 0033）。切り替えられるのは本部の管理者だけ（サーバでも弾く）。
export default function UserManagement({ user }) {
  const canEditSheet = !!user?.is_hq;
  const [users,    setUsers]    = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form,     setForm]     = useState({ login_id: '', password: '', name: '', role: 'user', job_type: 'both', ns_sheet: false });
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const load = () => api.getUsers().then(setUsers).catch(console.error);
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ login_id: '', password: '', name: '', role: 'user', job_type: 'both', ns_sheet: false });
    setEditUser(null); setShowForm(false); setError('');
  };

  const openEdit = (u) => {
    setForm({ login_id: u.login_id, password: '', name: u.name, role: u.role, job_type: u.job_type || 'both', ns_sheet: !!u.ns_sheet });
    setEditUser(u); setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (!form.name.trim()) { setError('氏名を入力してください'); return; }
    try {
      if (editUser) {
        const data = { name: form.name, role: form.role, job_type: form.job_type };
        if (canEditSheet) data.ns_sheet = form.ns_sheet ? 1 : 0;
        // ログインIDが変更された場合のみ送信
        if (form.login_id.trim() && form.login_id !== editUser.login_id) {
          data.login_id = form.login_id.trim();
        }
        if (form.password) data.password = form.password;
        await api.updateUser(editUser.id, data);
        setSuccess('ユーザーを更新しました');
      } else {
        if (!form.login_id.trim()) { setError('ログインIDを入力してください'); return; }
        if (!form.password)        { setError('パスワードを入力してください'); return; }
        const { ns_sheet, ...rest } = form;
        await api.createUser({ ...rest, login_id: form.login_id.trim(), ...(canEditSheet ? { ns_sheet: ns_sheet ? 1 : 0 } : {}) });
        setSuccess('ユーザーを作成しました');
      }
      resetForm(); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message); }
  };

  const handlePurge = async (u) => {
    if (!window.confirm(
      `「${u.name}」を完全削除します。\n\n` +
      'この操作は取り消せません。このユーザーのKPI記録・シフト・座席メモ・振り返りもすべて削除され、過去の集計から除外されます。\n\n' +
      '本当に完全削除しますか？'
    )) return;
    try { await api.deleteUser(u.id); load(); setSuccess('ユーザーを完全削除しました'); setTimeout(() => setSuccess(''), 3000); }
    catch (err) { alert(err.message); }
  };

  const toggleActive = async (u) => {
    if (u.is_active && !window.confirm(`「${u.name}」を無効化しますか？\n（ログインできなくなりますが、KPI記録は残り、いつでも有効化で復元できます）`)) return;
    try { await api.updateUser(u.id, { is_active: u.is_active ? 0 : 1 }); load(); }
    catch (err) { alert(err.message); }
  };

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inactiveCount = users.filter(u => !u.is_active).length;
  const visibleUsers = showInactive ? users : users.filter(u => u.is_active);

  const labelStyle = { fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink)' };
  const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 5 };

  const cardBase = {
    background: 'var(--field)',
    borderRadius: 12,
    border: '1px solid var(--rule-strong)',
    boxShadow: 'none',
  };

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <h1 className="page-title"style={{ marginBottom: 0 }}> ユーザー管理</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowInactive(s => !s)}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--rule-strong)', background: showInactive ? 'var(--mark-soft)' : 'var(--field)', color: showInactive ? 'var(--mark)' : 'var(--ink-soft)', fontSize: 'var(--t-note)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            {showInactive ? '無効ユーザーを表示中': `無効ユーザーを表示${inactiveCount ? `（${inactiveCount}）`: ''}`}
          </button>
          <button className="btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>＋ ユーザー追加</button>
        </div>
      </div>

      {success && <div className="alert-success">{success}</div>}

      {showForm && (
        <div style={{ ...cardBase, padding: '20px 22px', marginBottom: 16, borderColor: '#33406E' }} className="fade-in">
          <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 16 }}>
            {editUser ? 'ユーザー編集': 'ユーザー追加'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  ログインID <span style={{ color: 'var(--loss)', fontSize: 'var(--t-note)' }}>必須</span>
                  {editUser && <span style={{ color: 'var(--ink-mute)', fontWeight: 400, fontSize: 'var(--t-note)' }}>（変更する場合は上書き）</span>}
                </label>
                <input
                  className="form-input"
                  value={form.login_id}
                  onChange={e => F('login_id', e.target.value)}
                  placeholder="例: tanaka01"
                  required
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>氏名 <span style={{ color: 'var(--loss)', fontSize: 'var(--t-note)' }}>必須</span></label>
                <input className="form-input" value={form.name} onChange={e => F('name', e.target.value)} placeholder="例: 田中 太郎" required />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  パスワード {editUser && <span style={{ color: 'var(--ink-mute)', fontWeight: 400, fontSize: 'var(--t-note)' }}>（変更する場合のみ）</span>}
                </label>
                <input className="form-input" type="password" value={form.password} onChange={e => F('password', e.target.value)} placeholder={editUser ? '変更しない場合は空白' : '6文字以上推奨'} required={!editUser} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>権限</label>
                <select className="form-input" value={form.role} onChange={e => F('role', e.target.value)}>
                  <option value="user">スタッフ</option>
                  <option value="admin">管理者</option>
                </select>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>役割（現場）</label>
                <select className="form-input" value={form.job_type} onChange={e => F('job_type', e.target.value)}>
                  <option value="both">{JOB_TYPE_LABELS.both}</option>
                  <option value="catcher">{JOB_TYPE_LABELS.catcher}</option>
                  <option value="closer">{JOB_TYPE_LABELS.closer}</option>
                </select>
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginTop: 4, lineHeight: 1.5 }}>
                  キャッチャーは結果を入れずに着座メモを保存でき、獲得率は表示されません。
                </div>
              </div>
              {canEditSheet && (
                <div style={fieldStyle}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer', color: 'var(--ink)', fontSize: 14, fontWeight: 600 }}>
                    <input type="checkbox" checked={!!form.ns_sheet} onChange={e => F('ns_sheet', e.target.checked)} style={{ width: 20, height: 20 }} />
                    NSシートに載せる
                  </label>
                  <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginTop: 4, lineHeight: 1.5 }}>
                    オンにすると、この人の日報が NS_KPI スプレッドシートに載ります（毎時の同期で反映、過去の日報も載ります）。
                  </div>
                </div>
              )}
            </div>
            {error && <div className="alert-error">{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-secondary" onClick={resetForm}>キャンセル</button>
              <button type="submit" className="btn-primary">{editUser ? '更新する' : 'ユーザーを追加'}</button>
            </div>
          </form>
        </div>
      )}

      <div style={{ ...cardBase, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--t-note)', minWidth: 560 }}>
          <thead>
            <tr style={{ background: 'var(--field)' }}>
              {['名前','ログインID','権限','役割','状態','NSシート','作成日','操作'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-soft)', borderBottom: '1px solid var(--rule-strong)', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleUsers.length === 0 && (
              <tr><td colSpan={8} style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--ink-mute)' }}>
                {showInactive ? 'ユーザーがいません' : '有効なユーザーがいません'}
              </td></tr>
            )}
            {visibleUsers.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid var(--rule-strong)', opacity: u.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--ink)' }}>{u.name}</td>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                  <code style={{ background: 'var(--mark-soft)', padding: '2px 7px', borderRadius: 5, fontSize: 'var(--t-note)', fontFamily: 'monospace', color: 'var(--mark)', border: '1px solid #33406E' }}>{u.login_id}</code>
                </td>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                  <span className={`badge ${u.role === 'admin' ? 'badge-admin' : 'badge-user'}`}>
                    {u.role === 'admin' ? '管理者' : 'スタッフ'}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: 'var(--ink)', fontSize: 'var(--t-note)' }}>
                  {JOB_TYPE_LABELS[u.job_type] || JOB_TYPE_LABELS.both}
                </td>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                  <span className={`badge ${u.is_active ? 'badge-active' : 'badge-inactive'}`}>
                    {u.is_active ? '有効' : '無効'}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: u.ns_sheet ? 'var(--gain)' : 'var(--ink-mute)', fontWeight: u.ns_sheet ? 700 : 400 }}>
                  {u.ns_sheet ? '載せる' : '—'}
                </td>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: 'var(--ink-mute)' }}>{u.created_at?.slice(0, 10)}</td>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', gap: 5 }}>
                    <button className="btn-edit" onClick={() => openEdit(u)}>編集</button>
                    <button
                      style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--rule-strong)', background: u.is_active ? 'var(--field)' : 'var(--gain-bg)', color: u.is_active ? 'var(--ink-soft)' : 'var(--gain)', fontSize: 'var(--t-note)', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}
                      onClick={() => toggleActive(u)}
                    >{u.is_active ? '無効化' : '有効化'}</button>
                    {!u.is_active && (
                      <button className="btn-danger" onClick={() => handlePurge(u)} title="KPI記録ごと完全に削除します（復元不可）">完全削除</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
